/**
 * Logo Manager
 * 
 * Handles adding the Doctivity logo and footer to PDF exports.
 * Supports both PNG/JPEG and SVG images by converting SVG to canvas.
 */

// Import logo directly - webpack will handle this
import logoImage from 'assets/images/logo.png';

// Cached image data
let cachedLogo = null;

/**
 * Convert an SVG to a base64 PNG using canvas
 * 
 * @param {string} svgUrl - URL or data URL of the SVG
 * @param {number} width - Desired width
 * @param {number} height - Desired height
 * @returns {Promise<string>} - Base64 PNG data
 */
const svgToBase64PNG = (svgUrl, width = 300, height = 100) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      // Use higher resolution for crisp rendering
      const scale = 2;
      canvas.width = width * scale;
      canvas.height = height * scale;
      
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);
      ctx.drawImage(img, 0, 0, width, height);
      
      // Get base64 without the data URL prefix
      const dataUrl = canvas.toDataURL('image/png');
      const base64 = dataUrl.split(',')[1];
      resolve(base64);
    };
    
    img.onerror = (err) => {
      console.error('Failed to load SVG image:', err);
      reject(err);
    };
    
    img.src = svgUrl;
  });
};

/**
 * Load an image (SVG, PNG, or JPEG) and convert to base64 PNG
 * 
 * @param {string} imageUrl - URL to the image
 * @returns {Promise<{data: string, type: string} | null>}
 */
const loadImageAsBase64 = async (imageUrl, width, height) => {
  try {
    // Check if it's an SVG
    const isSvg = imageUrl.includes('.svg') || imageUrl.includes('image/svg');
    
    if (isSvg) {
      // Convert SVG to PNG via canvas
      const base64 = await svgToBase64PNG(imageUrl, width, height);
      return { data: base64, type: 'PNG' };
    } else {
      // Fetch and convert regular images
      const response = await fetch(imageUrl);
      if (!response.ok) {
        console.warn(`Failed to fetch image: ${response.status}`);
        return null;
      }
      
      const blob = await response.blob();
      const base64 = await blobToBase64(blob);
      
      let imageType = 'PNG';
      const contentType = response.headers.get('content-type');
      if (contentType?.includes('jpeg') || contentType?.includes('jpg')) {
        imageType = 'JPEG';
      }
      
      return { data: base64, type: imageType };
    }
  } catch (err) {
    console.error('Error loading image:', err);
    return null;
  }
};

/**
 * Convert a Blob to base64 string
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Load the Doctivity logo and convert to base64
 * Caches the result to avoid repeated conversions
 * 
 * @returns {Promise<{data: string, type: string} | null>}
 */
export const loadDoctivityLogo = async () => {
  if (cachedLogo) {
    return cachedLogo;
  }
  
  cachedLogo = await loadImageAsBase64(logoImage, 200, 60);
  return cachedLogo;
};

/**
 * Add the Doctivity logo to a PDF document header
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Object} options - Position and size options
 * @returns {Promise<number>} - Y position after logo (for layout purposes)
 */
export const addLogoToPDF = async (doc, options = {}) => {
  const {
    x = 40,
    y = 20,
    maxWidth = 100,  // Max width constraint
    maxHeight = 30,  // Max height constraint
  } = options;
  
  const logo = await loadDoctivityLogo();
  
  if (!logo) {
    return y;
  }
  
  try {
    // Original logo aspect ratio (261 x 59)
    const originalWidth = 261;
    const originalHeight = 59;
    const aspectRatio = originalWidth / originalHeight; // ~4.42
    
    // Calculate size that fits within maxWidth/maxHeight while maintaining ratio
    let width = maxWidth;
    let height = width / aspectRatio;
    
    // If height exceeds max, scale down based on height instead
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspectRatio;
    }
    
    doc.addImage(
      `data:image/${logo.type.toLowerCase()};base64,${logo.data}`,
      logo.type,
      x,
      y,
      width,
      height
    );
    
    return y + height + 10;
  } catch (err) {
    console.error('Failed to add logo to PDF:', err);
    return y;
  }
};

/**
 * Add footer to a specific page
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {number} pageNumber - Current page number
 * @param {number} totalPages - Total number of pages
 * @param {string} pdfType - 'slideshow' or 'printable'
 */
const addFooterToPage = (doc, pageNumber, totalPages, pdfType = 'printable') => {
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  
  // Sizing based on pdfType - compact to fit more chart rows
  const footerHeight = pdfType === 'slideshow' ? 24 : 18;
  const yellowBarHeight = pdfType === 'slideshow' ? 3 : 2;
  const fontSize = pdfType === 'slideshow' ? 8 : 5;
  const pageNumFontSize = pdfType === 'slideshow' ? 10 : 7;
  const textOffset = pdfType === 'slideshow' ? 16 : 12;
  const leftMargin = pdfType === 'slideshow' ? 20 : 15;
  const rightMargin = pdfType === 'slideshow' ? 30 : 20;
  
  const footerY = pageHeight - footerHeight - yellowBarHeight;
  
  // Yellow/gold bar at top of footer
  doc.setFillColor(234, 179, 8); // Yellow/gold color
  doc.rect(0, footerY, pageWidth, yellowBarHeight, 'F');
  
  // Teal/green background
  doc.setFillColor(17, 94, 89); // Teal color
  doc.rect(0, footerY + yellowBarHeight, pageWidth, footerHeight, 'F');
  
  // Copyright text on the left (white)
  doc.setFontSize(fontSize);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(255, 255, 255);
  doc.text(
    '© Doctivity Health • Proprietary & Confidential • Do Not Share or Reproduce',
    leftMargin,
    footerY + yellowBarHeight + textOffset
  );
  
  // Page number on the right (white)
  doc.setFontSize(pageNumFontSize);
  doc.text(
    String(pageNumber),
    pageWidth - rightMargin,
    footerY + yellowBarHeight + textOffset,
    { align: 'right' }
  );
};

/**
 * Add footer to all pages in the PDF
 * Call this after all content has been added to the PDF
 * 
 * @param {jsPDF} doc - The jsPDF document instance
 * @param {Object} options - Options including pdfType
 */
export const addFooterToAllPages = async (doc, options = {}) => {
  const { pdfType = 'printable' } = options;
  const totalPages = doc.internal.getNumberOfPages();
  
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    addFooterToPage(doc, i, totalPages, pdfType);
  }
};

/**
 * Preload logo into cache
 */
export const preloadLogo = async () => {
  await loadDoctivityLogo();
};

/**
 * Clear cached logo
 */
export const clearLogoCache = () => {
  cachedLogo = null;
};

/**
 * Set logo data directly
 */
export const setLogoData = (base64Data, imageType = 'PNG') => {
  cachedLogo = { data: base64Data, type: imageType };
};

export default {
  loadDoctivityLogo,
  addLogoToPDF,
  addFooterToAllPages,
  preloadLogo,
  clearLogoCache,
  setLogoData,
};